import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn, getFormatTime } from '@/lib/utils';
import { AssignDragGrip, AssignRowHint } from '@/pages/sale/components/assign-drag-grip';
import { useSaleAssignDraggable } from '@/pages/sale/hooks/use-sale-assign-draggable';
import { SaleAssignDragPayload } from '@/pages/sale/lib/sale-comment-drag';
import { QueueCustomer, QueueError } from '@/types/fb-type';
import { Copy, ExternalLink, FacebookIcon, Reply } from 'lucide-react';
import { useMemo, useState } from 'react';
import ReplyComponent from './ReplyComponent';

type Props = {
  q: QueueCustomer;
  active?: boolean;
  dragOver?: boolean;
  enableDropHighlight?: boolean;
  onSelect?: (q: QueueCustomer) => void;
  assignDraggable?: boolean;
  onDragOver?: React.DragEventHandler<HTMLLIElement>;
  onDragLeave?: React.DragEventHandler<HTMLLIElement>;
  onDrop?: React.DragEventHandler<HTMLLIElement>;
  onCopied?: (text: string) => void;
  className?: string;
  onReplyComment?: (commentId: string) => void;
};

function preview(s?: string | null, max = 120) {
  if (!s) return '';
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return t.slice(0, max - 1) + '…';
}

export function ParticipantQueueBubble({
  q,
  active,
  dragOver,
  enableDropHighlight,
  onSelect,
  assignDraggable = true,
  onDragOver,
  onDragLeave,
  onDrop,
  onCopied,
  className,
  onReplyComment,
}: Props) {
  const needsReply = q.queueError === QueueError.NeedReplyMsg;
  const replyFailed = q.queueError === QueueError.ErrorReply;
  const isMessage = (q.lastReferenceType || '').toUpperCase() === 'MESSAGE';
  const receivedPreview = preview(q.lastComment || '');
  const sentPreview = q.lastContent || '';
  const formattedTime = getFormatTime(q.createdAt);
  const commentUrl = q.permalinkUrl;
  const [isReplyingToComment, setIsReplyingToComment] = useState(false);

  const assignPayload = useMemo(
    () =>
      ({
        type: 'queue_customer',
        queueId: q.id,
        customerId: q.customerId,
        conversationId: '',
        name: q.name,
        username: '',
        lastMessage: null,
        lastComment: q.lastContent,
        lastActionTime: q.createdAt,
        source: 'queue',
      }) as SaleAssignDragPayload,
    [q],
  );

  const {
    setNodeRef,
    isSelected,
    draggableProps,
    handleRowClick,
    tapAssignEnabled,
    stateClassName,
  } = useSaleAssignDraggable(assignPayload, {
    disabled: !assignDraggable,
    enableTapSelect: true,
  });

  const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <li
      ref={setNodeRef}
      {...draggableProps}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={(event) => handleRowClick(event, () => onSelect?.(q))}
      className={cn(
        'group relative flex items-start gap-3 p-3 rounded-xl transition-all',
        'border border-slate-200 bg-white',
        'hover:bg-slate-50 hover:shadow-sm hover:-translate-y-0.5',
        active && 'ring-1 ring-blue-200 bg-blue-50/40',
        dragOver && enableDropHighlight && 'ring-1 ring-blue-300 bg-blue-50/40',
        stateClassName,
        !tapAssignEnabled && 'cursor-grab active:cursor-grabbing select-none',
        className,
      )}
    >
      {(needsReply || replyFailed) && (
        <span
          className={cn(
            'absolute left-0 top-2 bottom-2 w-1 rounded-full',
            needsReply && 'bg-amber-400',
            replyFailed && 'bg-red-500',
          )}
        />
      )}

      <AssignDragGrip className="mt-1 text-slate-400 group-hover:text-slate-700" />

      <Avatar className="h-10 w-10 border">
        <AvatarImage src={q.profileImageUrl || ''} alt={q.name || String(q.id)} />
        <AvatarFallback>{(q.name || '?').charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-sm truncate text-slate-900">{q.name || 'Unknown'}</div>
          {formattedTime && (
            <div className="text-[10px] text-slate-400 shrink-0">{formattedTime}</div>
          )}
        </div>

        <div className="text-xs text-slate-500 truncate">@{q.referenceId || 'na'}</div>

        <div className="mt-1 flex items-center gap-2 min-w-0">
          <span
            className={cn(
              'shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
              isMessage ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-700',
            )}
          >
            {isMessage ? 'Message' : 'Comment'}
          </span>

          <span className="block truncate text-sm font-semibold text-slate-900">
            {receivedPreview || (
              <span className="font-normal text-slate-400">No recent content</span>
            )}
          </span>
        </div>

        {!isReplyingToComment && (
          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
            <div className="text-[10px] font-semibold text-slate-500 mb-1">Last sent</div>
            <div className="text-xs text-slate-700 whitespace-pre-wrap">
              {sentPreview || <span className="text-slate-400">—</span>}
            </div>

            {!isMessage && commentUrl && (
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={(event) => {
                    stopRowClick(event);
                    window.open(commentUrl, '_blank', 'noopener,noreferrer');
                  }}
                  onPointerDown={(event) => event.stopPropagation()}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-800 hover:bg-slate-100 cursor-pointer"
                >
                  <FacebookIcon className="h-3.5 w-3.5 to-blue-900" />
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open FB
                </button>

                <button
                  type="button"
                  onClick={async (event) => {
                    stopRowClick(event);
                    await navigator.clipboard.writeText(commentUrl);
                    onCopied?.(commentUrl);
                  }}
                  onPointerDown={(event) => event.stopPropagation()}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-800 hover:bg-slate-100 cursor-pointer"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy link
                </button>

                <button
                  type="button"
                  onClick={(event) => {
                    stopRowClick(event);
                    setIsReplyingToComment(true);
                    if (onReplyComment && q.lastItem) onReplyComment(q.lastItem);
                  }}
                  onPointerDown={(event) => event.stopPropagation()}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-800 hover:bg-slate-100 cursor-pointer"
                >
                  <Reply className="h-3.5 w-3.5" />
                  Reply
                </button>

                <AssignRowHint
                  tapAssignEnabled={tapAssignEnabled}
                  isSelected={isSelected}
                  showDesktopHint
                  className="ml-auto opacity-70 sm:opacity-0 sm:group-hover:opacity-100"
                />
              </div>
            )}
          </div>
        )}

        {isReplyingToComment && q.lastItem && (
          <ReplyComponent
            authorName={q.name}
            commentId={q.lastItem}
            onClose={() => setIsReplyingToComment(false)}
          />
        )}
      </div>
    </li>
  );
}
